import { t as localeText } from "@/i18n/translate";
import type { CollectionDraft } from "@/api/asset-actions";
export { listSpaceMembers, getAsset, updateAssetMetadata, setAssetFavorite, deleteAssets, downloadAssets, listTags, createTag, setAssetTag, listCollections, createCollection, setAssetCollection, type CollectionDraft } from "@/api/asset-actions";
import { api } from "@/api/client";
import type { components } from "@/api/contract";
import { unwrap } from "@/api/errors";
export {
  type AssetUploadOptions,
  uploadAsset,
} from "@/api/assets";
import type {
  ReusableKind,
  V2ActivityItem,
  V2Asset,
  V2AssetMemberGrant,
  V2AssetShare,
  V2Collection,
  V2Generation,
  V2Me,
  V2Project,
  V2ProjectCreativeDecision,
  V2ProjectLibrary,
  V2ProjectCollaborator,
  V2RecipeFromAcceptedOutputRequest,
  V2RecipeVersion,
  V2Reusable,
  V2ReusableAuthorization,
  V2ReusableRequest,
  V2ReusableVersion,
  V2Space,
  V2SpaceInvitation,
  V2SpaceMember,
  V2SpaceMembershipEvent,
  V2SharedAsset,
  V2UsageSummary,
} from "../types";

export async function getV2Me(): Promise<V2Me> {
  return unwrap(await api.GET("/v1/me"));
}

export async function listSpaces(): Promise<V2Space[]> {
  return unwrap(await api.GET("/v1/spaces")).items;
}

export async function createSpace(name: string): Promise<V2Space> {
  return unwrap(await api.POST("/v1/spaces", { body: { name } }));
}


export async function listSpaceMembershipEvents(
  spaceID: string,
): Promise<V2SpaceMembershipEvent[]> {
  return unwrap(
    await api.GET("/v1/spaces/{id}/membership-events", {
      params: { path: { id: spaceID } },
    }),
  ).items;
}

export async function changeSpaceMemberRole(
  spaceID: string,
  member: V2SpaceMember,
  role: V2SpaceMember["role"],
): Promise<V2SpaceMember> {
  return unwrap(
    await api.PATCH("/v1/spaces/{id}/members/{user_id}", {
      params: { path: { id: spaceID, user_id: member.user_id } },
      body: { expected_version: member.version, role },
    }),
  );
}

export async function removeSpaceMember(
  spaceID: string,
  member: V2SpaceMember,
): Promise<void> {
  unwrap(
    await api.DELETE("/v1/spaces/{id}/members/{user_id}", {
      params: { path: { id: spaceID, user_id: member.user_id } },
      body: { expected_version: member.version, custody_decisions: [] },
    }),
  );
}

export async function transferSpaceOwnership(
  spaceID: string,
  actor: V2SpaceMember,
  target: V2SpaceMember,
): Promise<void> {
  unwrap(
    await api.POST("/v1/spaces/{id}/ownership-transfer", {
      params: { path: { id: spaceID } },
      body: {
        target_user_id: target.user_id,
        actor_expected_version: actor.version,
        target_expected_version: target.version,
      },
    }),
  );
}

export async function listSpaceInvitations(spaceID: string): Promise<V2SpaceInvitation[]> {
  return unwrap(
    await api.GET("/v1/spaces/{id}/invitations", { params: { path: { id: spaceID } } }),
  ).items;
}

export async function createSpaceInvitation(
  spaceID: string,
  email: string,
  role: V2SpaceMember["role"],
): Promise<components["schemas"]["V2SpaceInvitationReceipt"]> {
  return unwrap(
    await api.POST("/v1/spaces/{id}/invitations", {
      params: { path: { id: spaceID } },
      body: { email, role, expires_in_hours: 72 },
    }),
  );
}

export async function revokeSpaceInvitation(
  spaceID: string,
  invitationID: string,
): Promise<void> {
  unwrap(
    await api.DELETE("/v1/spaces/{id}/invitations/{invitation_id}", {
      params: { path: { id: spaceID, invitation_id: invitationID } },
    }),
  );
}

export async function acceptSpaceInvitation(token: string): Promise<V2Space> {
  return unwrap(await api.POST("/v1/invitations/accept", { body: { token } }));
}

export interface AssetFilters {
  mediaType?: V2Asset["media_type"];
  origin?: V2Asset["origin"];
  status?: "pending" | "available" | "archived" | "deleting" | "deletion_failed" | "missing";
  search?: string;
  favorite?: boolean;
  projectID?: string;
  collectionID?: string;
  reusableID?: string;
  tagID?: string;
  creatorID?: string;
  integrityState?: V2Asset["integrity_state"];
  createdAfter?: string;
  createdBefore?: string;
  sort?: "newest" | "oldest" | "name" | "recently_updated" | "favorites_first";
}

export async function listAssetsPage(filters: AssetFilters = {}, options: { cursor?: string; limit?: number; signal?: AbortSignal } = {}) {
  const baseQuery: {
    limit: number;
    cursor?: string;
    media_type?: V2Asset["media_type"];
    origin?: V2Asset["origin"];
    status?: AssetFilters["status"];
    search?: string;
    favorite?: boolean;
    project_id?: string;
    collection_id?: string;
    reusable_id?: string;
    tag_id?: string;
    creator_id?: string;
    integrity_state?: V2Asset["integrity_state"];
    created_after?: string;
    created_before?: string;
    sort?: AssetFilters["sort"];
  } = { limit: options.limit ?? 100 };
  if (filters.mediaType) baseQuery.media_type = filters.mediaType;
  if (filters.origin) baseQuery.origin = filters.origin;
  if (filters.status) baseQuery.status = filters.status;
  if (filters.search) baseQuery.search = filters.search;
  if (filters.favorite !== undefined) baseQuery.favorite = filters.favorite;
  if (filters.projectID) baseQuery.project_id = filters.projectID;
  if (filters.collectionID) baseQuery.collection_id = filters.collectionID;
  if (filters.reusableID) baseQuery.reusable_id = filters.reusableID;
  if (filters.tagID) baseQuery.tag_id = filters.tagID;
  if (filters.creatorID) baseQuery.creator_id = filters.creatorID;
  if (filters.integrityState) baseQuery.integrity_state = filters.integrityState;
  if (filters.createdAfter) baseQuery.created_after = filters.createdAfter;
  if (filters.createdBefore) baseQuery.created_before = filters.createdBefore;
  if (filters.sort) baseQuery.sort = filters.sort;

  return unwrap(await api.GET("/v1/assets", {
    params: { query: { ...baseQuery, ...(options.cursor ? { cursor: options.cursor } : {}) } },
    signal: options.signal,
  }));
}

/** Full enumeration for exports and bulk actions; browsing requests pages on demand. */
export async function listAssets(filters: AssetFilters = {}, signal?: AbortSignal): Promise<V2Asset[]> {
  const assets: V2Asset[] = [];
  let cursor: string | undefined;
  const seenCursors = new Set<string>();
  do {
    signal?.throwIfAborted();
    const page = await listAssetsPage(filters, { cursor, signal });
    assets.push(...page.items);
    cursor = page.next_cursor;
    if (cursor) {
      if (seenCursors.has(cursor)) {
        throw new Error(localeText("interface.asset_pagination_returned_a_repeated_cursor_3a3454c6", { defaultValue: "Asset pagination returned a repeated cursor." }));
      }
      seenCursors.add(cursor);
    }
  } while (cursor);
  return assets;
}


export async function findGenerationForAsset(
  assetID: string,
  mediaType?: V2Asset["media_type"],
): Promise<V2Generation | null> {
  let cursor: string | undefined;
  const seenCursors = new Set<string>();
  do {
    const page = unwrap(
      await api.GET("/v1/generations", {
        params: {
          query: {
            limit: 100,
            ...(mediaType ? { media_type: mediaType } : {}),
            ...(cursor ? { cursor } : {}),
          },
        },
      }),
    );
    const match = page.items.find((generation) =>
      generation.parts.some((part) =>
        part.attempts.some((attempt) => attempt.output_asset_id === assetID || attempt.outputs?.some((output) => output.asset_id === assetID)),
      ),
    );
    if (match) return match;
    cursor = page.next_cursor || undefined;
    if (cursor) {
      if (seenCursors.has(cursor)) {
        throw new Error(localeText("interface.generation_pagination_returned_a_repeated_cursor_47cedfe4", { defaultValue: "Generation pagination returned a repeated cursor." }));
      }
      seenCursors.add(cursor);
    }
  } while (cursor);
  return null;
}

/** Load the complete immutable decision history used by Project Library readback. */
export async function listProjectCreativeDecisions(
  projectID: string,
): Promise<V2ProjectCreativeDecision[]> {
  const decisions: V2ProjectCreativeDecision[] = [];
  const seenIDs = new Set<string>();
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = unwrap(
      await api.GET("/v1/projects/{id}/decisions", {
        params: {
          path: { id: projectID },
          query: { limit: 100, ...(cursor ? { cursor } : {}) },
        },
      }),
    );
    for (const decision of page.items) {
      if (seenIDs.has(decision.id)) continue;
      seenIDs.add(decision.id);
      decisions.push(decision);
    }
    cursor = page.next_cursor || undefined;
    if (cursor) {
      if (seenCursors.has(cursor)) {
        throw new Error(localeText("interface.project_decision_pagination_returned_a_repeated_7934fa26", { defaultValue: "Project decision pagination returned a repeated cursor." }));
      }
      seenCursors.add(cursor);
    }
  } while (cursor);
  return decisions;
}

/** Load the bounded, creator-safe Project graph used by Library readback. */
export async function getProjectLibrary(
  projectID: string,
  signal?: AbortSignal,
): Promise<V2ProjectLibrary> {
  return unwrap(
    await api.GET("/v1/projects/{id}/library", {
      signal,
      params: { path: { id: projectID } },
    }),
  );
}



export async function setAssetArchived(asset: V2Asset, archived: boolean): Promise<V2Asset> {
  return unwrap(
    await api.PATCH("/v1/assets/{id}", {
      params: { path: { id: asset.id } },
      body: {
        expected_version: asset.version,
        status: archived ? "archived" : "available",
      },
    }),
  );
}

function versionReferences(assets: V2Asset[]) {
  return assets.map((asset) => ({ id: asset.id, expected_version: asset.version }));
}

export async function archiveAssets(assets: V2Asset[]): Promise<void> {
  unwrap(
    await api.POST("/v1/assets/bulk/archive", {
      body: { assets: versionReferences(assets) },
    }),
  );
}

export async function restoreAssets(assets: V2Asset[]): Promise<void> {
  unwrap(
    await api.POST("/v1/assets/bulk/restore", {
      body: { assets: versionReferences(assets) },
    }),
  );
}



export async function createAssetShare(
  assetID: string,
  permission: "view" | "download",
  expiresAt?: string,
): Promise<components["schemas"]["V2AssetShareReceipt"]> {
  return unwrap(
    await api.POST("/v1/assets/{id}/shares", {
      params: { path: { id: assetID } },
      body: { permission, ...(expiresAt ? { expires_at: expiresAt } : {}) },
    }),
  );
}

export async function listAssetShares(assetID: string): Promise<V2AssetShare[]> {
  return unwrap(
    await api.GET("/v1/assets/{id}/shares", {
      params: { path: { id: assetID } },
    }),
  ).items;
}

export async function revokeAssetShare(assetID: string, shareID: string): Promise<void> {
  unwrap(
    await api.DELETE("/v1/assets/{id}/shares/{share_id}", {
      params: { path: { id: assetID, share_id: shareID } },
    }),
  );
}

export async function listAssetGrants(assetID: string): Promise<V2AssetMemberGrant[]> {
  return unwrap(
    await api.GET("/v1/assets/{id}/grants", {
      params: { path: { id: assetID } },
    }),
  ).items;
}

export async function setAssetGrant(
  assetID: string,
  userID: string,
  permission: "view" | "download",
): Promise<V2AssetMemberGrant> {
  return unwrap(
    await api.POST("/v1/assets/{id}/grants", {
      params: { path: { id: assetID } },
      body: { user_id: userID, permission },
    }),
  );
}

export async function revokeAssetGrant(assetID: string, grantID: string): Promise<void> {
  unwrap(
    await api.DELETE("/v1/assets/{id}/grants/{grant_id}", {
      params: { path: { id: assetID, grant_id: grantID } },
    }),
  );
}

export async function getSharedAsset(token: string): Promise<V2SharedAsset> {
  return unwrap(
    await api.GET("/v1/shares/{token}", {
      params: { path: { token } },
    }),
  );
}








export async function updateCollection(
  collection: V2Collection,
  draft: CollectionDraft,
  status: "active" | "archived" = collection.status,
): Promise<V2Collection> {
  return unwrap(
    await api.PUT("/v1/collections/{id}", {
      params: { path: { id: collection.id } },
      body: {
        expected_version: collection.version,
        name: draft.name,
        description: draft.description,
        kind: collection.kind,
        visibility: draft.visibility,
        query: collection.kind === "smart" ? (draft.query ?? {}) : {},
        status,
        parent_id: draft.parentID ?? null,
        ...(collection.project_id ? { project_id: collection.project_id } : {}),
      },
    }),
  );
}

export async function deleteCollection(collection: V2Collection): Promise<void> {
  unwrap(
    await api.DELETE("/v1/collections/{id}", {
      params: { path: { id: collection.id } },
      body: { expected_version: collection.version },
    }),
  );
}

export async function listProjects(): Promise<V2Project[]> {
  const projects: V2Project[] = [];
  let cursor: string | undefined;
  const seenCursors = new Set<string>();
  do {
    const page = unwrap(
      await api.GET("/v1/projects", {
        params: {
          query: { limit: 100, ...(cursor ? { cursor } : {}) },
        },
      }),
    );
    projects.push(...page.items);
    cursor = page.next_cursor || undefined;
    if (cursor) {
      if (seenCursors.has(cursor)) {
        throw new Error(localeText("interface.project_pagination_returned_a_repeated_cursor_e9a8a9fe", { defaultValue: "Project pagination returned a repeated cursor." }));
      }
      seenCursors.add(cursor);
    }
  } while (cursor);
  return projects;
}

export async function createProject(
  name: string,
  description: string,
  visibility: V2Project["visibility"],
  color: V2Project["color"] = "blue",
): Promise<V2Project> {
  return unwrap(
    await api.POST("/v1/projects", {
      body: { name, description, visibility, color },
    }),
  );
}

export async function setProjectArchived(project: V2Project, archived: boolean): Promise<V2Project> {
  return unwrap(
    await api.PATCH("/v1/projects/{id}", {
      params: { path: { id: project.id } },
      body: {
        expected_version: project.version,
        status: archived ? "archived" : "active",
      },
    }),
  );
}

export async function cloneProject(project: V2Project): Promise<V2Project> {
  return unwrap(
    await api.POST("/v1/projects/{id}/clone", {
      params: { path: { id: project.id } },
      body: { name: `${project.name} copy` },
    }),
  );
}

export async function deleteProject(projectID: string): Promise<void> {
  unwrap(
    await api.DELETE("/v1/projects/{id}", {
      params: { path: { id: projectID } },
    }),
  );
}

export async function listProjectCollaborators(
  projectID: string,
): Promise<V2ProjectCollaborator[]> {
  return unwrap(
    await api.GET("/v1/projects/{id}/collaborators", {
      params: { path: { id: projectID } },
    }),
  ).items;
}

export async function setProjectCollaborator(
  projectID: string,
  userID: string,
  permission: V2ProjectCollaborator["permission"],
): Promise<V2ProjectCollaborator> {
  return unwrap(
    await api.PUT("/v1/projects/{id}/collaborators", {
      params: { path: { id: projectID } },
      body: { user_id: userID, permission },
    }),
  );
}

export async function removeProjectCollaborator(
  projectID: string,
  userID: string,
): Promise<void> {
  unwrap(
    await api.DELETE("/v1/projects/{id}/collaborators/{user_id}", {
      params: { path: { id: projectID, user_id: userID } },
    }),
  );
}

export async function listActivityPage(page = 1) {
  return unwrap(await api.GET("/v1/activity", { params: { query: { limit: 20, page } } }));
}

export async function listActivity(): Promise<V2ActivityItem[]> {
  return unwrap(
    await api.GET("/v1/activity", { params: { query: { limit: 100 } } }),
  ).items;
}

export async function getUsage(): Promise<V2UsageSummary> {
  return unwrap(await api.GET("/v1/usage"));
}

export async function listReusable(kind: ReusableKind): Promise<V2Reusable[]> {
  const params = { params: { query: { limit: 100 } } };
  switch (kind) {
    case "avatar":
      return unwrap(await api.GET("/v1/creators", params)).items;
    case "product":
      return unwrap(await api.GET("/v1/products", params)).items;
    case "brand":
      return unwrap(await api.GET("/v1/brands", params)).items;
    case "voice":
      return unwrap(await api.GET("/v1/voices", params)).items;
    case "preset":
      return unwrap(await api.GET("/v1/recipes", params)).items;
    case "reference_set":
      return unwrap(await api.GET("/v1/references", params)).items;
  }
}

export async function createReusable(
  kind: ReusableKind,
  body: V2ReusableRequest,
): Promise<V2Reusable> {
  switch (kind) {
    case "avatar":
      return unwrap(await api.POST("/v1/creators", { body }));
    case "product":
      return unwrap(await api.POST("/v1/products", { body }));
    case "brand":
      return unwrap(await api.POST("/v1/brands", { body }));
    case "voice":
      return unwrap(await api.POST("/v1/voices", { body }));
    case "preset":
      throw new Error(localeText("interface.recipes_must_be_saved_from_an_accepted_output_ebf64bf8", { defaultValue: "Recipes must be saved from an accepted output." }));
    case "reference_set":
      return unwrap(await api.POST("/v1/references", { body }));
  }
}

export async function updateReusable(
  kind: Exclude<ReusableKind, "preset">,
  id: string,
  body: V2ReusableRequest,
): Promise<V2Reusable> {
  switch (kind) {
    case "avatar": return unwrap(await api.PUT("/v1/creators/{id}", { params: { path: { id } }, body }));
    case "product": return unwrap(await api.PUT("/v1/products/{id}", { params: { path: { id } }, body }));
    case "brand": return unwrap(await api.PUT("/v1/brands/{id}", { params: { path: { id } }, body }));
    case "voice": return unwrap(await api.PUT("/v1/voices/{id}", { params: { path: { id } }, body }));
    case "reference_set": return unwrap(await api.PUT("/v1/references/{id}", { params: { path: { id } }, body }));
  }
}

export async function createReusableAuthorization(
  kind: "avatar" | "voice",
  id: string,
  body: components["schemas"]["V2ReusableAuthorizationRequest"],
): Promise<V2ReusableAuthorization> {
  if (kind === "avatar") {
    return unwrap(
      await api.POST("/v1/creators/{id}/authorizations", {
        params: { path: { id } },
        body,
      }),
    );
  }
  return unwrap(
    await api.POST("/v1/voices/{id}/authorizations", {
      params: { path: { id } },
      body,
    }),
  );
}

export async function listReusableAuthorizations(
  kind: "avatar" | "voice",
  id: string,
): Promise<V2ReusableAuthorization[]> {
  if (kind === "avatar") {
    return unwrap(
      await api.GET("/v1/creators/{id}/authorizations", {
        params: { path: { id } },
      }),
    ).items;
  }
  return unwrap(
    await api.GET("/v1/voices/{id}/authorizations", {
      params: { path: { id } },
    }),
  ).items;
}

export async function revokeReusableAuthorization(
  kind: "avatar" | "voice",
  reusableID: string,
  authorization: V2ReusableAuthorization,
  reason: string,
): Promise<V2ReusableAuthorization> {
  const body: components["schemas"]["V2ReusableAuthorizationRevoke"] = {
    expected_version: authorization.version,
    reason: reason.trim(),
  };
  if (kind === "avatar") {
    return unwrap(
      await api.POST(
        "/v1/creators/{id}/authorizations/{authorization_id}/revoke",
        {
          params: {
            path: { id: reusableID, authorization_id: authorization.id },
          },
          body,
        },
      ),
    );
  }
  return unwrap(
    await api.POST(
      "/v1/voices/{id}/authorizations/{authorization_id}/revoke",
      {
        params: {
          path: { id: reusableID, authorization_id: authorization.id },
        },
        body,
      },
    ),
  );
}

export async function prepareReusable(
  kind: "avatar" | "voice",
  id: string,
  authorization: V2ReusableAuthorization,
): Promise<components["schemas"]["V2ReusablePreparationReceipt"]> {
  const mediaType = kind === "avatar"
    ? (["image", "video"] as const).find((media) =>
        authorization.allowed_media.includes(media),
      ) ?? "image"
    : "audio";
  const body: components["schemas"]["V2ReusablePreparationRequest"] = {
    authorization_id: authorization.id,
    media_type: mediaType,
    operation:
      authorization.allowed_operations[0] ??
      (kind === "avatar" ? "generate" : "speech"),
  };
  if (kind === "avatar") {
    return unwrap(
      await api.POST("/v1/creators/{id}/prepare", {
        params: { path: { id } },
        body,
      }),
    );
  }
  return unwrap(
    await api.POST("/v1/voices/{id}/prepare", {
      params: { path: { id } },
      body,
    }),
  );
}

export async function listReusableVersions(
  kind: ReusableKind,
  id: string,
): Promise<V2ReusableVersion[]> {
  const params = { params: { path: { id } } };
  switch (kind) {
    case "avatar":
      return unwrap(await api.GET("/v1/creators/{id}/versions", params)).items;
    case "product":
      return unwrap(await api.GET("/v1/products/{id}/versions", params)).items;
    case "brand":
      return unwrap(await api.GET("/v1/brands/{id}/versions", params)).items;
    case "voice":
      return unwrap(await api.GET("/v1/voices/{id}/versions", params)).items;
    case "preset":
      return unwrap(await api.GET("/v1/recipes/{id}/versions", params)).items.map(
        (recipe) => ({
          id: recipe.version_id,
          reusable_id: recipe.reusable_id,
          kind: "preset" as const,
          version_number: recipe.version_number,
          display_name: recipe.name,
          display_name_status: "exact" as const,
          status: "ready",
          material_snapshot: {},
          source_snapshot: recipe.sources,
          content_fingerprint: recipe.fingerprint,
          source_fingerprint: null,
          qualified_at: recipe.created_at,
          revoked_at: null,
          created_at: recipe.created_at,
        }),
      );
    case "reference_set":
      return unwrap(await api.GET("/v1/references/{id}/versions", params)).items;
  }
}

export async function getReusableVersion(
  kind: Exclude<ReusableKind, "preset">,
  id: string,
  versionID: string,
): Promise<V2ReusableVersion> {
  const params = { params: { path: { id, version_id: versionID } } };
  switch (kind) {
    case "avatar":
      return unwrap(await api.GET("/v1/creators/{id}/versions/{version_id}", params));
    case "product":
      return unwrap(await api.GET("/v1/products/{id}/versions/{version_id}", params));
    case "brand":
      return unwrap(await api.GET("/v1/brands/{id}/versions/{version_id}", params));
    case "voice":
      return unwrap(await api.GET("/v1/voices/{id}/versions/{version_id}", params));
    case "reference_set":
      return unwrap(await api.GET("/v1/references/{id}/versions/{version_id}", params));
  }
}

export async function createRecipeFromAcceptedOutput(
  body: V2RecipeFromAcceptedOutputRequest,
  idempotencyKey: string,
): Promise<V2RecipeVersion> {
  return unwrap(
    await api.POST("/v1/recipes/from-accepted-output", {
      params: { header: { "Idempotency-Key": idempotencyKey } },
      body,
    }),
  );
}

export async function getRecipeVersion(
  id: string,
  versionID: string,
): Promise<V2RecipeVersion> {
  return unwrap(
    await api.GET("/v1/recipes/{id}/versions/{version_id}", {
      params: { path: { id, version_id: versionID } },
    }),
  );
}
